import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Hub } from 'aws-amplify/utils';
import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  confirmSignIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  type AuthUser,
} from 'aws-amplify/auth';

export interface AppAuthUser {
  username: string;
  userId: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authStateSubject = new BehaviorSubject<boolean>(false);

  readonly isAuthenticated$ = this.authStateSubject.asObservable();

  constructor() {
    void this.refreshAuthState();
    Hub.listen('auth', () => {
      void this.refreshAuthState();
    });
  }

  private cleanEmail(email: string): string {
    if (!email) throw new Error('Email is required');
    const cleaned = email
      .normalize('NFKC')
      .trim()
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/^["'<>\s]+|["'<>\s]+$/g, '')
      .toLowerCase();
    if (!cleaned) throw new Error('Email is required');
    const emailRegex = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;
    if (!emailRegex.test(cleaned)) {
      throw new Error('Please enter a valid email address');
    }
    return cleaned;
  }

  async refreshAuthState(): Promise<void> {
    try {
      await getCurrentUser();
      this.authStateSubject.next(true);
    } catch {
      this.authStateSubject.next(false);
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  async signUp(email: string, password: string) {
    const username = this.cleanEmail(email);
    return signUp({
      username,
      password,
      options: {
        userAttributes: { email: username },
      },
    });
  }

  async confirmSignUp(email: string, code: string) {
    const username = this.cleanEmail(email);
    return confirmSignUp({ username, confirmationCode: code });
  }

  async resendSignUpCode(email: string) {
    const username = this.cleanEmail(email);
    return resendSignUpCode({ username });
  }

  async signIn(email: string, password: string) {
    const username = this.cleanEmail(email);
    const result = await signIn({ username, password });
    await this.refreshAuthState();
    return result;
  }

  /**
   * Completes sign-in when Cognito responds with CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
   * (temporary/admin-set password that must be changed before first use).
   */
  async completeNewPassword(newPassword: string) {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    await this.refreshAuthState();
    return result;
  }

  async signOut() {
    await signOut();
    await this.refreshAuthState();
  }

  /** Alias used by header / older call sites. */
  async logout(): Promise<void> {
    await this.signOut();
  }

  async getCurrentUser(): Promise<AppAuthUser> {
    const user: AuthUser = await getCurrentUser();
    return {
      username: user.username,
      userId: user.userId,
    };
  }

  async resetPassword(email: string) {
    const username = this.cleanEmail(email);
    return resetPassword({ username });
  }

  async confirmResetPassword(email: string, newPassword: string, confirmationCode: string) {
    const username = this.cleanEmail(email);
    return confirmResetPassword({ username, newPassword, confirmationCode });
  }

  async getCognitoGroups(): Promise<string[]> {
    try {
      const session = await fetchAuthSession();
      const rawGroups = session.tokens?.accessToken?.payload?.['cognito:groups'];
      if (Array.isArray(rawGroups)) {
        return rawGroups.filter((g): g is string => typeof g === 'string');
      }
      if (typeof rawGroups === 'string') {
        return [rawGroups];
      }
      return [];
    } catch {
      return [];
    }
  }

  async getUserGroupsWithFallback(username: string): Promise<string[]> {
    const groups = await this.getCognitoGroups();
    if (groups.length > 0) {
      return groups;
    }
    const assignedGroup = this.getUserAssignedGroup(username);
    return assignedGroup ? [assignedGroup] : ['customer'];
  }

  resolvePostLoginRoute(groups: string[]): string {
    if (groups.includes('admin')) {
      return '/admin';
    }
    if (groups.includes('company')) {
      return '/backoffice';
    }
    return '/customer';
  }

  /**
   * Admin flow: create Cognito user and store pending group assignment.
   */
  async signUpWithGroup(email: string, password: string, groupName: string) {
    const username = this.cleanEmail(email);
    const result = await signUp({
      username,
      password,
      options: {
        userAttributes: { email: username },
      },
    });

    this.storeGroupAssignment(username, groupName);
    try {
      await this.assignUserToGroup(username, groupName);
    } catch (error) {
      console.warn('Group assignment deferred until user exists in Cognito:', error);
    }

    return { ...result, groupName };
  }

  private storeGroupAssignment(username: string, groupName: string) {
    const assignments = this.getStoredGroupAssignments();
    assignments[username] = {
      groupName,
      timestamp: new Date().toISOString(),
      status: 'pending',
    };
    localStorage.setItem('pendingGroupAssignments', JSON.stringify(assignments));
  }

  private getStoredGroupAssignments(): Record<string, unknown> {
    const stored = localStorage.getItem('pendingGroupAssignments');
    return stored ? JSON.parse(stored) : {};
  }

  async processPendingGroupAssignments(username: string) {
    const assignments = this.getStoredGroupAssignments();
    const assignment = assignments[username] as
      | { groupName: string; status: string }
      | undefined;

    if (assignment?.status === 'pending') {
      try {
        await this.assignUserToGroup(username, assignment.groupName);
        assignment.status = 'processed';
        assignments[username] = assignment;
        localStorage.setItem('pendingGroupAssignments', JSON.stringify(assignments));
      } catch (error) {
        console.error(`Failed to process group assignment for ${username}:`, error);
      }
    }
  }

  getUserAssignedGroup(username: string): string | null {
    const assignments = this.getStoredGroupAssignments();
    const assignment = assignments[username] as { groupName: string } | undefined;
    return assignment?.groupName ?? null;
  }

  clearProcessedAssignments() {
    const assignments = this.getStoredGroupAssignments();
    const cleaned = Object.fromEntries(
      Object.entries(assignments).filter(
        ([, assignment]) => (assignment as { status: string }).status !== 'processed'
      )
    );
    localStorage.setItem('pendingGroupAssignments', JSON.stringify(cleaned));
  }

  async assignUserToGroup(username: string, groupName: string) {
    const functionUrl = await this.getAdminFunctionUrl();
    const headers = await this.getAdminHeaders();

    const response = await fetch(`${functionUrl}/assign-group`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ username, groupName }),
    });

    if (!response.ok) {
      throw new Error(`Failed to assign user to group: ${response.statusText}`);
    }

    return response.json();
  }

  async getUserGroups(username: string) {
    try {
      const functionUrl = await this.getAdminFunctionUrl();
      if (!functionUrl || functionUrl.includes('your-admin-function-url')) {
        return [];
      }

      const response = await fetch(
        `${functionUrl}/user-groups?username=${encodeURIComponent(username)}`,
        {
          method: 'GET',
          headers: await this.getAdminHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get user groups: ${response.statusText}`);
      }

      const result = await response.json();
      return result.groups || [];
    } catch (error) {
      console.warn('Could not fetch user groups from server:', error);
      return [];
    }
  }

  async deleteUserFromCognito(username: string) {
    const functionUrl = await this.getAdminFunctionUrl();
    const headers = await this.getAdminHeaders();

    const response = await fetch(`${functionUrl}/delete-user`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ username }),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete user from Cognito: ${response.statusText}`);
    }

    return response.json();
  }

  async getAdminFunctionUrl(): Promise<string> {
    const outputs: any = await import('../../../amplify_outputs.json');
    const url = outputs?.custom?.adminFunctionUrl || outputs?.default?.custom?.adminFunctionUrl;
    if (!url) {
      throw new Error('Admin function URL is not configured. Deploy the Amplify backend first.');
    }
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  private async getAdminHeaders(): Promise<Record<string, string>> {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      throw new Error('Admin operation requires a signed-in user');
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }
}
